import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGlobalFilter, usePagination, useTable, useSortBy } from 'react-table';
import { CLIENTS } from '../../constants/columns';
import { clientOptions } from '../../constants/filterOptions';
import { setFilters } from '../../utils/setFilters';
import useTableUrlSync, { useInitialTableQuery } from '../../hooks/useTableUrlState';
import { saveFilterSnapshot, clearFilterSnapshot, readFilterValues } from '../../utils/filterSnapshot';
import { sortClientsParams } from '../../constants/sortParams';
import toNameCase from '../../utils/toNameCase';

// components
import Empty from '../../components/Empty/Empty';
import Header from '../../components/Header/Header';
import Table from '../../components/Table/Table';
import TableTabs from '../../components/TableTabs/TableTabs';
import HookForm from '../../components/HookForm/HookForm';

// icons
import getClients from '../../api/clients/getClients';
import clientsIcon from '../../brand/generated/sidebar/clients.svg';
import ClientsFilter from '../../components/Filters/ClientsFilter';
import { ROLE_USER } from '../../constants/roles';
const tableStyle = { backgroundColor: 'white', minWidth: 2000 };

const FILTER_NAMES = [...clientOptions.map((option) => option.name), 'all'];
const SNAPSHOT_KEY = 'clients';

const Clients = () => {
  const [columns, setColumns] = React.useState(CLIENTS);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [clients, setClients] = React.useState([]);
  const [fastSearchClients, setFastSearchClients] = React.useState(null);
  const [isFetchAll, setFetchAll] = useState(false);
  const [dataCount, setDataCount] = useState(0);
  const fromUrl = useInitialTableQuery(FILTER_NAMES);
  const [pagination, setPagination] = useState({
    page: fromUrl.page,
    totalPages: 1,
    hasPreviousPage: false,
    hasNextPage: false,
    pageSize: fromUrl.pageSize || 10,
  });

  const fetchData = (params) => {
    setLoading(true);
    getClients(params)
      .then((res) => {
        setClients(res.data.users);
        setPagination({
          ...res.data.pagination,
          page: res.data.pagination?.pageNumber,
          pageSize: pagination.pageSize,
        });
        setDataCount(res.data.pagination?.count);
      })
      .finally(() => setLoading(false));
  };

  const table = useTable(
    {
      columns,
      data: clients,
      manualPagination: true,
      pageCount: pagination.totalPages,
      page: pagination.page - 1,
      disableSortRemove: true,
    },
    useGlobalFilter,
    useSortBy,
    usePagination
  );
  const { setGlobalFilter, page, setPageSize } = table;

  const [value, setValue] = useState(fromUrl.filters.all || '');
  const [queryParams, setQueryParams] = useState(fromUrl.filters);
  const filtersFormRef = React.useRef(null);
  useTableUrlSync({ queryParams, page: pagination.page, pageSize: pagination.pageSize });

  const applyFilters = (resetFilters) => {
    setFilters(resetFilters, setFetchAll, setPageSize, setPagination, setQueryParams, clientOptions);

    if (resetFilters) {
      // Ð¤Ð¾ÑÐ¼Ð° Ð¼Ð¾Ð½ÑÐ¸ÑÑÐµÑÑÑ Ð¸Ð· defaultValue â Ð±ÐµÐ· Ð¾ÑÐ¸ÑÑÐºÐ¸ Ð²Ð¾ÑÑÑÐ°Ð½Ð¾Ð²Ð»ÐµÐ½Ð½ÑÐµ Ð¸Ð· Ð°Ð´ÑÐµÑÐ°
      // Ð·Ð½Ð°ÑÐµÐ½Ð¸Ñ Ð²ÐµÑÐ½ÑÐ»Ð¸ÑÑ Ð±Ñ Ð¿ÑÐ¸ ÑÐ»ÐµÐ´ÑÑÑÐµÐ¼ Ð¾ÑÐºÑÑÑÐ¸Ð¸ Ð¿Ð°Ð½ÐµÐ»Ð¸.
      clientOptions.forEach((option) => delete option.defaultValue);
      clearFilterSnapshot(SNAPSHOT_KEY);
    } else {
      saveFilterSnapshot(SNAPSHOT_KEY, clientOptions);
    }
  };

  // Ð¤Ð¸Ð»ÑÑÑÑ Ð¸Ð· Ð°Ð´ÑÐµÑÐ°: Ð¿Ð¾Ð´Ð¿Ð¸ÑÐ¸ Ðº id Ð±ÐµÑÑÐ¼ Ð¸Ð· ÑÐ½Ð¸Ð¼ÐºÐ° Ð¿Ð¾ÑÐ»ÐµÐ´Ð½ÐµÐ³Ð¾ Ð¿ÑÐ¸Ð¼ÐµÐ½ÐµÐ½Ð¸Ñ.
  React.useEffect(() => {
    const restored = readFilterValues(SNAPSHOT_KEY, fromUrl.filters);
    clientOptions.forEach((option) => {
      const value = restored[option.name];
      if (value === undefined) return;
      option.value = value;
      option.defaultValue = value;
      filtersFormRef.current?.setValue(option.name, value);
    });
    // Ð¢Ð¾Ð»ÑÐºÐ¾ Ð½Ð° Ð¼Ð¾Ð½ÑÐ¸ÑÐ¾Ð²Ð°Ð½Ð¸Ð¸: Ð´Ð°Ð»ÑÑÐµ Ð·Ð½Ð°ÑÐµÐ½Ð¸ÑÐ¼Ð¸ ÑÐ°ÑÐ¿Ð¾ÑÑÐ¶Ð°ÐµÑÑÑ ÑÐ¾ÑÐ¼Ð°.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const fastSearchDebounceTimerRef = React.useRef(null);

  const makeFastSearchVariants = (input) => {
    const raw = (input ?? '').trim();
    if (!raw) return [];

    return Array.from(
      new Set([
        raw,
        raw.toLocaleLowerCase('ru-RU'),
        raw.toLocaleUpperCase('ru-RU'),
        toNameCase(raw),
      ])
    ).filter(Boolean);
  };

  const isPhoneQuery = (q) =>
    !!q && ((q.length === 18 && q[0] === '+') || (q.length === 17 && q[0] === '7'));

  const getFastSearchKey = (params) => {
    const { page, pageSize, ...rest } = params || {};
    return JSON.stringify(rest);
  };

  const lastFastSearchKeyRef = React.useRef(null);

  const paginate = (items, pageNumber, pageSize) => {
    const start = Math.max(0, (pageNumber - 1) * pageSize);
    const end = start + pageSize;
    return items.slice(start, end);
  };

  const fetchAllByVariant = async (params) => {
    const variants = makeFastSearchVariants(params?.all);
    if (!variants.length) return [];

    const merged = [];
    const seen = new Set();

    for (const all of variants) {
      let pageNumber = 1;
      let totalPages = 1;

      do {
        // keep pageSize reasonably high to reduce number of requests
        const res = await getClients({
          ...params,
          all,
          page: pageNumber,
          pageSize: 200,
        });

        const users = res?.data?.users || [];
        users.forEach((u) => {
          if (!u?.id) return;
          if (seen.has(u.id)) return;
          seen.add(u.id);
          merged.push(u);
        });

        totalPages = res?.data?.pagination?.totalPages ?? 1;
        pageNumber += 1;
      } while (pageNumber <= totalPages);
    }

    return merged;
  };

  const fetchDataByAllVariants = async (params) => {
    setLoading(true);
    try {
      const merged = await fetchAllByVariant(params);
      setFastSearchClients(merged);
      setDataCount(merged.length);

      const totalPages = Math.max(1, Math.ceil(merged.length / pagination.pageSize));
      setPagination((prev) => ({
        ...prev,
        page: 1,
        totalPages,
        hasPreviousPage: false,
        hasNextPage: totalPages > 1,
      }));
      setClients(paginate(merged, 1, pagination.pageSize));
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    if (!isFetchAll) {
      const params = { roleId: ROLE_USER, page: pagination.page, pageSize: pagination.pageSize, ...queryParams };

      const fastSearchMode = params?.all && !isPhoneQuery(params.all);

      if (fastSearchMode) {
        const key = getFastSearchKey(params);
        if (lastFastSearchKeyRef.current !== key) {
          lastFastSearchKeyRef.current = key;
          fetchDataByAllVariants(params);
        } else if (fastSearchClients) {
          setClients(paginate(fastSearchClients, pagination.page, pagination.pageSize));
          const totalPages = Math.max(1, Math.ceil(fastSearchClients.length / pagination.pageSize));
          setPagination((prev) => ({
            ...prev,
            totalPages,
            hasPreviousPage: pagination.page > 1,
            hasNextPage: pagination.page < totalPages,
          }));
          setDataCount(fastSearchClients.length);
        }
      } else {
        lastFastSearchKeyRef.current = null;
        setFastSearchClients(null);
        fetchData(params);
      }
    }
  }, [pagination.page, pagination.pageSize, queryParams]);

  const addClient = () => {
    navigate('/client/create-pkb');
  };

  const handleNavigate = (index) => {
    const client = clients.filter((item) => item.id === index);
    navigate(`client/${client[0].id}/main-data`, { state: client[0] });
  };

  const sortBy = (value) => {
    if (!sortClientsParams.get(value)) return;
    if (sortClientsParams.get(value) === queryParams.sortBy) {
      queryParams.sortBy = '';
    } else {
      queryParams.sortBy = sortClientsParams.get(value);
    }
    setFetchAll(false);
    setQueryParams({ ...queryParams });
  };

  const onFastSearch = (value) => {
    if (fastSearchDebounceTimerRef.current) {
      clearTimeout(fastSearchDebounceTimerRef.current);
      fastSearchDebounceTimerRef.current = null;
    }

    //check if value is phone number
    if ((value.length === 18 && value[0] === '+') || (value.length === 17 && value[0] === '7')) {
      value = value.replace(/\D/g, '');
    }

    setPagination((prev) => ({ ...prev, page: 1 }));
    setQueryParams((prev) => ({
      ...prev,
      all: value,
    }));
  };

  React.useEffect(() => {
    const next = (value ?? '').trim();
    const current = (queryParams?.all ?? '').trim();
    if (next === current) return;

    if (fastSearchDebounceTimerRef.current) {
      clearTimeout(fastSearchDebounceTimerRef.current);
    }

    fastSearchDebounceTimerRef.current = setTimeout(() => {
      onFastSearch(value);
    }, 400);

    return () => {
      if (fastSearchDebounceTimerRef.current) {
        clearTimeout(fastSearchDebounceTimerRef.current);
        fastSearchDebounceTimerRef.current = null;
      }
    };
  }, [value]);

  return (
    <>
      <Header title="ÐÐ»Ð¸ÐµÐ½ÑÑ" icon={clientsIcon} />
      <br />
      <br />
      <br />
      <br />
      <TableTabs
        setFilter={onFastSearch}
        value={value}
        setValue={setValue}
        filterComponent={
          <HookForm
            ref={filtersFormRef}
            inputs={clientOptions}
            grid={{
              display: 'grid',
              gap: '10px',
              gridTemplateColumns: '1fr 1fr 1fr 1fr',
            }}
            responsiveForm
            style={{ backgroundColor: 'transparent' }}
          />
        }
        handleSearchParams={applyFilters}
        defaultFiltersOpen={Object.keys(fromUrl.filters).some((name) => name !== 'all')}
        navigate={addClient}
        button={'ÐÐ¾Ð±Ð°Ð²Ð¸ÑÑ ÐºÐ»Ð¸ÐµÐ½ÑÐ°'}
        inputPlaceholder={'ÐÑÑÑÑÑÐ¹ Ð¿Ð¾Ð¸ÑÐº Ð¿Ð¾ ÐºÐ»Ð¸ÐµÐ½ÑÐ°Ð¼'}
        hotspotItem="client"
        hotspotHint="addClient"
      />
      <br />
      {!page.length && !loading ? (
        <Empty />
      ) : (
        <Table
          p={pagination}
          table={table}
          style={tableStyle}
          handleNavigate={handleNavigate}
          pagination
          sort
          setPagination={setPagination}
          setFetchAll={setFetchAll}
          isFetchAll={isFetchAll}
          setData={setClients}
          loading={loading}
          setLoading={setLoading}
          columns={columns}
          setColumns={setColumns}
          columnsSettingsKey="clients"
          queryParams={queryParams}
          api={'/api/crm/clients/getClients'}
          response={'users'}
          isSortable
          dataCount={dataCount}
          handleSortTable={sortBy}
        />
      )}
    </>
  );
};

export default Clients;
